import { EntityType } from '@repo/common/enums/entityType';
import type { Position } from '@xyflow/react';
import { useCallback, useMemo } from 'react';
import {
	useLineageFlowContext,
	useLineageFlowUIContext,
} from '../../context/LineageFlowProvider/LineageFlowContext';
import { isLineageFlowNode } from './EntityNode.types';

export const useEntityNode = (id: string) => {
	const { createAccess, onAddTemporaryNode } = useLineageFlowContext();

	const handleAddTemporaryNode = useCallback(
		(position: Position) => {
			onAddTemporaryNode({
				nodeId: id,
				position,
			});
		},
		[id, onAddTemporaryNode]
	);

	return {
		createAccess,
		handleAddTemporaryNode,
	};
};

export const useEntityNodeHeader = (id: string) => {
	const {
		rootID,
		focusedNodeID,
		creationQueries,
		monitors,
		tests,
		reactFlowInstance,
		onFetchImpactedChildren,
	} = useLineageFlowContext();
	const {
		nodeId,
		showMonitorsAndTests,
		showCreationQuery,
		isFullscreen,
		nodeActions,
		openCreationQuery,
		openMonitorsAndTests,
		closeAllModals,
	} = useLineageFlowUIContext();

	const node = reactFlowInstance?.getNode(id);

	const entityCreationQueries = creationQueries.get(id);
	const entityMonitors = monitors.get(id);
	const entityTests = tests.get(id);

	const filteredTests = useMemo(
		() =>
			entityTests?.filter((test) => test.latest_run && test.latest_run.status),
		[entityTests]
	);

	const hasMonitorsOrTests = Boolean(
		filteredTests?.length || entityMonitors?.length
	);

	const areMonitorsAndTestsSuccessful = useMemo(
		() =>
			(filteredTests || []).every((test) => test.latest_run?.success) &&
			(entityMonitors || []).every((monitor) => monitor.status === 'normal'),
		[filteredTests, entityMonitors]
	);

	const handleOpenCreationQuery = useCallback(() => {
		openCreationQuery(id);
	}, [openCreationQuery, id]);

	const handleOpenMonitorsAndTests = useCallback(() => {
		openMonitorsAndTests(id);
	}, [openMonitorsAndTests, id]);

	const handleCloseAllModals = useCallback(() => {
		closeAllModals();
	}, [closeAllModals]);

	const handleViewImpactingColumns = useCallback(() => {
		onFetchImpactedChildren({
			impactChildID: id,
		});
	}, [onFetchImpactedChildren, id]);

	return {
		creationQueries: entityCreationQueries ?? [],
		monitors: entityMonitors,
		tests: filteredTests,
		isChartNode:
			node && isLineageFlowNode(node)
				? node.data.entity_type === EntityType.chart
				: false,
		isRootNode: id === rootID,
		isFocused: id === focusedNodeID,
		isCurrentModal: nodeId === id,
		isFullscreen,
		showCreationQuery,
		showMonitorsAndTests,
		hasMonitorsOrTests,
		areMonitorsAndTestsSuccessful,
		nodeActions,
		handleOpenCreationQuery,
		handleOpenMonitorsAndTests,
		handleCloseAllModals,
		handleViewImpactingColumns,
	};
};
